/**
 * DeleteDocumentLinkDialog — modal de confirm destructive pour hard DELETE (Chunk 9.L.13).
 *
 * Backend : `DELETE /v1/document-links/:id` (9.J) avec gating Couche 2
 * (`document_link.delete` default `agent_allowed=false` seedée 0036 destructive,
 * broker JWT bypass naturel).
 *
 * UX (PO décision Q5 9.L.13) :
 *   - Confirm destructive simple — pas de reason textarea (le schéma `document_links`
 *     n'a pas de waiver_reason ; backend DELETE = hard sans audit reason).
 *   - Warning amber explicite : action **hard DELETE irréversible Phase 1**
 *     (table sans `deleted_at`, dette P3 9.J soft-delete column reportée).
 *   - Submit bouton red destructive (`bg-red-600`).
 *   - Esc + backdrop click → close (sauf pendant submit).
 *   - Focus initial sur Cancel (anti-clic-réflexe destructive).
 *
 * Erreurs API (bilingues) :
 *   - 404 DOCUMENT_LINKS_NOT_FOUND              → bandeau red (déjà supprimé race)
 *   - 403 GATING_HUMAN_REQUIRED                 → bandeau red (rare broker JWT bypass naturel)
 */

import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useDeleteDocumentLink } from "../../../lib/queries/documentLinks";
import { toast } from "../../../lib/toast";

type Props = {
  readonly documentLinkId: string;
  readonly displayLabel: string | null;
  readonly onClose: () => void;
};

export function DeleteDocumentLinkDialog({
  documentLinkId,
  displayLabel,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const del = useDeleteDocumentLink();
  const { reset: resetMutation } = del;

  useEffect(() => {
    resetMutation();
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !del.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, del.isPending]);

  const onConfirm = () => {
    if (del.isPending) return;
    del.mutate(
      { documentLinkId },
      {
        onSuccess: () => {
          toast.success(t("document_links.delete_dialog.success"));
          onClose();
        },
      },
    );
  };

  const apiErrorMsg =
    del.error instanceof ApiException
      ? getApiErrorMessage(del.error, lang) || t("errors.generic")
      : del.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!del.isPending) onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("document_links.delete_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {displayLabel?.trim() || t("document_links.delete_dialog.untitled")}
          </p>
          <p className="text-sm text-amber-700">
            {t("document_links.delete_dialog.warning")}
          </p>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={del.isPending}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={del.isPending}
            aria-busy={del.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
          >
            {del.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.25"
                />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {del.isPending
              ? t("document_links.delete_dialog.submitting")
              : t("document_links.delete_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
